/**
 * Messages between a page's widget and widgets in its iframes ("frame mode").
 *
 * Only frames on the same root domain as the page (go.example.com ↔
 * stream.example.com), or origins listed in data-frame-origins, take part:
 * the page ignores messages from anything else, and a frame only talks to a
 * parent it allows. Frames never call the ReadingVox API themselves; the page's
 * widget reads their text with its own key and quota.
 */
import type { WordHighlightInfo } from '../core/StreamingPlayer';

export const RV_FRAME = 'readingvox-frame';
export const FRAME_PROTOCOL_VERSION = 1;

/** Rect in the sender's viewport coordinates */
export type FrameRect = { left: number; top: number; width: number; height: number };

export type ToParent =
  | { rv: typeof RV_FRAME; v: number; kind: 'hello' }
  | { rv: typeof RV_FRAME; kind: 'text'; text: string }
  | { rv: typeof RV_FRAME; kind: 'selection'; text: string; rect: FrameRect | null }
  | { rv: typeof RV_FRAME; kind: 'speak'; text: string };

export type HighlightStyle = { mode?: string; word?: string; sentence?: string };

export type ToChild =
  | { rv: typeof RV_FRAME; kind: 'ack' }
  /** Sent by a parent whose widget started after the frame gave up waiting */
  | { rv: typeof RV_FRAME; v: number; kind: 'invite' }
  | { rv: typeof RV_FRAME; kind: 'highlightWord'; info: WordHighlightInfo }
  | { rv: typeof RV_FRAME; kind: 'highlightSentence'; sentence: string }
  | { rv: typeof RV_FRAME; kind: 'clear' }
  | { rv: typeof RV_FRAME; kind: 'style'; style: HighlightStyle; autoScroll: boolean }
  | { rv: typeof RV_FRAME; kind: 'clickListen'; on: boolean }
  | { rv: typeof RV_FRAME; kind: 'clearSelection' };

export function isFrameMessage(data: unknown): data is { rv: string; kind: string } {
  return !!data && typeof data === 'object' && (data as { rv?: unknown }).rv === RV_FRAME
    && typeof (data as { kind?: unknown }).kind === 'string';
}

/** Second-level labels under which registrations happen one level deeper (example.co.uk) */
const SECOND_LEVEL = new Set(['co', 'com', 'org', 'net', 'ac', 'edu', 'gov', 'sch', 'k12', 'ltd', 'plc']);

/**
 * Root domain of a hostname: definedlearning.com for go.definedlearning.com,
 * school.co.uk for www.school.co.uk. Approximate (no public-suffix list), which
 * only matters for unusual suffixes; data-frame-origins covers those.
 */
export function rootDomain(hostname: string): string {
  const host = hostname.toLowerCase().replace(/\.$/, '');
  if (/^[\d.]+$/.test(host) || host.includes(':') || !host.includes('.')) return host; // IPs, localhost
  const labels = host.split('.');
  const n = labels.length >= 3 && SECOND_LEVEL.has(labels[labels.length - 2]) && labels[labels.length - 1].length === 2 ? 3 : 2;
  return labels.slice(-n).join('.');
}

/**
 * May `origin` take part in frame mode with a page/frame on `ownHostname`?
 * Same root domain (https only, or both on localhost), or listed in `extra`
 * (full origins like "https://partner.example.org").
 */
export function originAllowed(origin: string, ownHostname: string, extra: string[] = []): boolean {
  let url: URL;
  try { url = new URL(origin); } catch { return false; }
  if (extra.some((o) => o.replace(/\/+$/, '').toLowerCase() === url.origin.toLowerCase())) return true;
  const local = (h: string) => h === 'localhost' || h === '127.0.0.1' || h.endsWith('.localhost');
  if (local(url.hostname) && local(ownHostname)) return true;
  if (url.protocol !== 'https:') return false;
  return rootDomain(url.hostname) === rootDomain(ownHostname);
}
